import { useEffect, useState, type FormEvent } from 'react';
import { apiFetch } from '~/lib/api';
import {
  JOB_TYPES,
  SALARY_PERIODS,
  buildPreferencesPayload,
  readCountries,
  salaryRangeError,
  toForm,
  type JobPreferences,
  type PreferencesForm,
  type SalaryPeriod,
} from './lib/preferences';
import { Link } from 'react-router-dom';
import { WaitingInline } from '~/components/ui';
import { messages, useT } from '~/i18n';
import WhatsappPhoneCard from './components/WhatsappPhoneCard';
import ImmigrationStatusCard from './components/ImmigrationStatusCard';
import EuAuthorizationCard from './components/EuAuthorizationCard';

/**
 * Employment preferences. Una tab de Perfil: el título y la frase los pone
 * `JobApplyProfileLayout`, así que esta pantalla arranca directo en el
 * formulario.
 *
 * The screen this replaces was a mock: inputs with no state, a save button that
 * did nothing. Everything here writes.
 *
 * El consentimiento de la bolsa de talento VIVÍA acá, como la última tarjeta
 * debajo de siete campos, y se mudó a `JobApplyTalentPoolPage`. La razón por la
 * que siempre fueron dos endpoints es la misma por la que ahora son dos
 * pantallas: guardar el formulario no puede mover el interruptor que decide
 * quién puede ver a esta persona, y mover el interruptor no puede depender de
 * que el formulario esté válido. Consentir no es una preferencia.
 */

const EMPTY_FORM: PreferencesForm = toForm(null);

export default function JobApplyPreferencesPage() {
  const t = useT('onboarding');
  const [form, setForm] = useState<PreferencesForm>(EMPTY_FORM);
  /** Lo guardado tal cual: lo lee la tarjeta de situación migratoria. */
  const [stored, setStored] = useState<JobPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch('/job-apply/api/dashboard')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data?.membership) return;
        const preferences = (data.membership.preferences ?? null) as JobPreferences | null;
        setStored(preferences);
        setForm(toForm(preferences));
      })
      .catch(() => {
        if (!cancelled) setErr(messages().onboarding.preferences.loadFailed);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const set = <K extends keyof PreferencesForm>(key: K, value: PreferencesForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const toggleJobType = (value: string) =>
    setForm((prev) => ({
      ...prev,
      job_types: prev.job_types.includes(value)
        ? prev.job_types.filter((t) => t !== value)
        : [...prev.job_types, value],
    }));

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    setNotice(null);

    const rangeError = salaryRangeError(form);
    if (rangeError) {
      setErr(rangeError);
      return;
    }

    setSaving(true);
    try {
      const res = await apiFetch('/job-apply/api/preferences', {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(buildPreferencesPayload(form)),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(body?.error || t.preferences.saveFailed);
        return;
      }
      setForm(toForm(body.preferences as JobPreferences | null));
      setNotice(t.preferences.saved);
    } catch {
      setErr(t.preferences.connectionError);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <WaitingInline label={t.preferences.loading} />;

  return (
    <div className="space-y-4">
      {err && <div className="alert alert-error text-sm">{err}</div>}
      {notice && <div className="alert alert-success text-sm">{notice}</div>}

      {/* Su WhatsApp: lo pide el alta y acá se cambia. Va primero porque es
          por donde le escribimos (y por donde llama a la evaluación de inglés). */}
      <WhatsappPhoneCard />

      {/* La situación migratoria (H-2) que se pidió en el alta: verla y
          cambiarla. Sólo a quien postula a EE. UU.; guarda con su PATCH. */}
      {readCountries(stored).includes('US') && <ImmigrationStatusCard preferences={stored} />}

      {/* Los papeles de la UE: sólo a quien eligió Europa. Esta SÍ mueve el envío. */}
      {readCountries(stored).includes('EU') && <EuAuthorizationCard preferences={stored} />}

      {/* Quien busca "países" acá lo encuentra, pero no se elige acá: esta
          pantalla es la de las agencias y no mueve el envío; los países sí. */}
      <p className="text-sm hush">
        {t.preferences.countriesPointer}{' '}
        <Link to="/panel" className="link font-medium">{t.preferences.countriesPointerLink}</Link>
      </p>

      <form onSubmit={save}>
        <div className="card">
          <div className="card-body space-y-4">
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">{t.preferences.rolesLabel}</span>
              </label>
              <input
                type="text"
                placeholder={t.preferences.rolesPlaceholder}
                className="input"
                value={form.target_roles}
                onChange={(e) => set('target_roles', e.target.value)}
              />
              <label className="label">
                <span className="label-text-alt">{t.preferences.rolesHint}</span>
              </label>
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">{t.preferences.locationsLabel}</span>
              </label>
              <input
                type="text"
                placeholder={t.preferences.locationsPlaceholder}
                className="input"
                value={form.target_locations}
                onChange={(e) => set('target_locations', e.target.value)}
              />
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">{t.preferences.jobTypesLabel}</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {JOB_TYPES.map((type) => (
                  <label key={type} className="label cursor-pointer gap-2">
                    <input
                      type="checkbox"
                      className="checkbox checkbox-primary checkbox-sm"
                      checked={form.job_types.includes(type)}
                      onChange={() => toggleJobType(type)}
                    />
                    <span className="label-text">{t.preferences.jobTypes[type]}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">{t.preferences.industriesLabel}</span>
              </label>
              <input
                type="text"
                placeholder={t.preferences.industriesPlaceholder}
                className="input"
                value={form.industries}
                onChange={(e) => set('industries', e.target.value)}
              />
            </div>

            <div>
              <label className="label">
                <span className="label-text font-medium">{t.preferences.salaryLabel}</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <input
                  type="number"
                  min={0}
                  placeholder={t.preferences.salaryMinPlaceholder}
                  className="input"
                  value={form.salary_min}
                  onChange={(e) => set('salary_min', e.target.value)}
                />
                <input
                  type="number"
                  min={0}
                  placeholder={t.preferences.salaryMaxPlaceholder}
                  className="input"
                  value={form.salary_max}
                  onChange={(e) => set('salary_max', e.target.value)}
                />
                <select
                  className="select"
                  value={form.salary_per}
                  onChange={(e) => set('salary_per', e.target.value as SalaryPeriod)}
                >
                  {SALARY_PERIODS.map((period) => (
                    <option key={period} value={period}>{t.preferences.salaryPeriods[period]}</option>
                  ))}
                </select>
                <input
                  type="text"
                  maxLength={3}
                  placeholder={t.preferences.currencyPlaceholder}
                  className="input uppercase"
                  value={form.salary_currency}
                  onChange={(e) => set('salary_currency', e.target.value)}
                />
              </div>
              <label className="label">
                <span className="label-text-alt">{t.preferences.salaryHint}</span>
              </label>
            </div>

            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">{t.preferences.excludedLabel}</span>
              </label>
              <input
                type="text"
                placeholder={t.preferences.excludedPlaceholder}
                className="input"
                value={form.excluded_companies}
                onChange={(e) => set('excluded_companies', e.target.value)}
              />
              <label className="label">
                <span className="label-text-alt">{t.preferences.excludedHint}</span>
              </label>
            </div>

            <div className="card-actions justify-end mt-4">
              <button className="btn btn-primary" type="submit" disabled={saving}>
                {saving && <span className="loading loading-spinner loading-sm" />}
                {t.preferences.submit}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
